import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { FiSearch, FiX, FiExternalLink, FiHeart } from "react-icons/fi";
import { PaginationControl } from "react-bootstrap-pagination-control";
import {
  PRODUCT_LIST,
  PRODUCT_LIST_BUY,
  CREATE_WISH_LIST,
  GET_WISH_LIST_AUCTION,
  GET_WISH_LIST_BUYNOW,
} from "../../constant";
import { GET, POST } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { getAuthUserId } from "../../services/localStorage";
import { sanitizeImageUrl } from "../../utils/sanitizeImageUrl";
import DetailDrawer from "../DetailDrawer";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const TABS = [
  {
    key: "auction",
    name: "Auction floor",
    wishAction: GET_WISH_LIST_AUCTION,
    wishQuery: "type=auction",
    productAction: PRODUCT_LIST,
    productLabel: Labels.product_list,
    viewPath: "/dealers/dashbord/product",
  },
  {
    key: "buynow",
    name: "Buy now floor",
    wishAction: GET_WISH_LIST_BUYNOW,
    wishQuery: "type=buynow",
    productAction: PRODUCT_LIST_BUY,
    productLabel: Labels.buynow_list,
    viewPath: "/dealers/dashbord/productbuy",
  },
];

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `€${num.toLocaleString()}`;
}

function productRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  // Some responses nest list one level deeper
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.products)) return payload.products;
  return [];
}

function buildFloorParams(tabKey, userId) {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const params = new URLSearchParams({
    page: "1",
    page_size: "50",
    timezone,
    sort_cars: "1",
    user_id: String(userId),
  });
  if (tabKey === "auction") {
    params.set("auction_status", "Upcoming Auction,Live Auction");
  }
  return params.toString();
}

function matchesSearch(item, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.product_name,
    item?.name,
    item?.brand_name,
    item?.model_name,
    item?.fuel_type,
    item?.registration,
    item?.registration_number,
    item?.status,
    item?.id,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

function AddWatchlist({
  user_id,
  auctionPayload,
  buyPayload,
  fetchingList,
  fetchingBuyList,
  wishList,
  wishListbuy,
  getProducts,
  getWishList,
  createWishList,
}) {
  const [tabIdx, setTabIdx] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [addingId, setAddingId] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);

  const tab = TABS[tabIdx];
  const id = user_id || getAuthUserId();
  const sourceList =
    tab.key === "auction"
      ? productRows(auctionPayload)
      : productRows(buyPayload);
  const watchedList =
    tab.key === "auction"
      ? Array.isArray(wishList)
        ? wishList
        : []
      : Array.isArray(wishListbuy)
      ? wishListbuy
      : [];

  const watchedIds = useMemo(() => {
    const set = new Set();
    watchedList.forEach((item) => {
      const pid = item?.product_id ?? item?.id;
      if (pid != null) set.add(String(pid));
    });
    return set;
  }, [watchedList]);

  const floorLoading =
    tab.key === "auction" ? Boolean(fetchingList) : Boolean(fetchingBuyList);
  const isLoading = floorLoading && sourceList.length === 0;

  // Prefetch auction + buy-now floors (and wishlists) so Buy now is not empty after refresh
  useEffect(() => {
    if (!id) return;
    TABS.forEach((floor) => {
      getProducts(
        floor.productAction,
        floor.productLabel,
        buildFloorParams(floor.key, id)
      );
      getWishList(id, floor.wishQuery, floor.wishAction);
    });
  }, [id]);

  // Refresh the active floor when switching tabs
  useEffect(() => {
    if (!id) return;
    getProducts(
      tab.productAction,
      tab.productLabel,
      buildFloorParams(tab.key, id)
    );
    getWishList(id, tab.wishQuery, tab.wishAction);
  }, [tabIdx]);

  useEffect(() => {
    setPage(1);
    setSearch("");
    setSortBy("name_asc");
    setViewingItem(null);
  }, [tabIdx]);

  useEffect(() => {
    setPage(1);
  }, [search, sortBy, pageSize]);

  const filteredList = useMemo(() => {
    let rows = [...sourceList].filter((item) => matchesSearch(item, search));
    rows.sort((a, b) => {
      const nameA = String(a?.product_name || a?.name || "");
      const nameB = String(b?.product_name || b?.name || "");
      const priceA = Number(a?.product_price ?? a?.start_price ?? a?.price) || 0;
      const priceB = Number(b?.product_price ?? b?.start_price ?? b?.price) || 0;
      if (sortBy === "price_desc") return priceB - priceA;
      if (sortBy === "price_asc") return priceA - priceB;
      if (sortBy === "name_desc") return nameB.localeCompare(nameA);
      return nameA.localeCompare(nameB);
    });
    return rows;
  }, [sourceList, search, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filteredList.length / pageSize) || 1);
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, filteredList.length);
  const pageRows = filteredList.slice(pageStart, pageEnd);
  const hasActiveFilters = Boolean(search) || sortBy !== "name_asc";

  const handleAdd = (item, index) => {
    const productId = item?.id ?? item?.product_id;
    if (!id || !productId || addingId) return;
    if (watchedIds.has(String(productId))) return;
    setAddingId(String(productId));
    createWishList({
      user: id,
      product: productId,
      product_index: pageStart + index,
    });
    setTimeout(() => {
      getWishList(id, tab.wishQuery, tab.wishAction);
      setAddingId(null);
    }, 900);
  };

  return (
    <div className="dd-section">
      <div className="dd-wl-intro">
        <span className="dd-wl-intro__badge" aria-hidden>
          <FiHeart size={18} />
        </span>
        <div>
          <h2>Find lots to watch</h2>
          <p>
            Browse the live floor and add vehicles to your active watchlist.
            Saved lots also appear under Favourites.
          </p>
        </div>
      </div>

      <nav className="dd-tabs" aria-label="Watchlist floors">
        {TABS.map((item, index) => (
          <button
            key={item.key}
            type="button"
            className={`dd-tab${index === tabIdx ? " is-active" : ""}`}
            onClick={() => setTabIdx(index)}
          >
            {item.name}
            <span className="dd-tab__count">
              {item.key === "auction"
                ? productRows(auctionPayload).length
                : productRows(buyPayload).length}
            </span>
          </button>
        ))}
      </nav>

      {isLoading ? (
        <div className="dd-empty" aria-busy="true">
          <strong>Loading floor…</strong>
          <p>Fetching {tab.name.toLowerCase()} lots you can watch.</p>
        </div>
      ) : (
        <>
          <div className="dd-toolbar">
            <label className="dd-toolbar__search">
              <FiSearch size={16} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, brand, reg…"
                aria-label="Search lots"
              />
              {search ? (
                <button
                  type="button"
                  className="dd-toolbar__clear-input"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              ) : null}
            </label>

            <label className="dd-toolbar__field">
              <span>Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name_asc">Name · A–Z</option>
                <option value="name_desc">Name · Z–A</option>
                <option value="price_desc">Price · high to low</option>
                <option value="price_asc">Price · low to high</option>
              </select>
            </label>

            <label className="dd-toolbar__field">
              <span>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            {hasActiveFilters ? (
              <button
                type="button"
                className="dd-btn dd-btn--ghost"
                onClick={() => {
                  setSearch("");
                  setSortBy("name_asc");
                  setPage(1);
                }}
              >
                Reset
              </button>
            ) : null}
          </div>

          <div className="dd-toolbar__meta">
            {filteredList.length ? (
              <span>
                Showing {pageStart + 1}–{pageEnd} of {filteredList.length}
              </span>
            ) : (
              <span>No matching lots</span>
            )}
          </div>

          {!sourceList.length ? (
            <div className="dd-empty">
              <strong>No lots on this floor</strong>
              <p>Check back when new auction or buy-now stock is listed.</p>
            </div>
          ) : !filteredList.length ? (
            <div className="dd-empty">
              <strong>No matches</strong>
              <p>Try a different search or reset filters.</p>
            </div>
          ) : (
            <>
              <div className="dd-table-wrap dd-wl-table">
                <table className="dd-table">
                  <thead>
                    <tr>
                      <th scope="col">Vehicle</th>
                      <th scope="col">Price</th>
                      <th scope="col">Status</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((item, index) => {
                      const productId = item?.id ?? item?.product_id;
                      const title =
                        item?.product_name || item?.name || "Untitled lot";
                      const image = sanitizeImageUrl(
                        item?.main_image || item?.product_image
                      );
                      const price = formatMoney(
                        item?.product_price ?? item?.start_price ?? item?.price
                      );
                      const status = item?.status || tab.name;
                      const watched = watchedIds.has(String(productId));
                      const busy = addingId === String(productId);

                      return (
                        <tr key={productId || `${tab.key}-${pageStart + index}`}>
                          <td>
                            <div className="dd-fav-vehicle">
                              <span
                                className="dd-fav-vehicle__thumb"
                                aria-hidden
                              >
                                {image ? (
                                  <img src={image} alt="" />
                                ) : (
                                  <span className="dd-fav-vehicle__placeholder">
                                    —
                                  </span>
                                )}
                              </span>
                              <div className="dd-fav-vehicle__copy">
                                <strong>{title}</strong>
                                <span>
                                  {[item?.brand_name, item?.model_name, item?.year]
                                    .filter(Boolean)
                                    .join(" · ") || "—"}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{price}</td>
                          <td>
                            <span
                              className={`dd-chip ${
                                tab.key === "auction"
                                  ? "dd-chip--auction"
                                  : "dd-chip--buynow"
                              }`}
                            >
                              {status}
                            </span>
                          </td>
                          <td>
                            <div className="dd-actions">
                              <button
                                type="button"
                                className="dd-action"
                                title="View details"
                                onClick={() => setViewingItem(item)}
                              >
                                <FiExternalLink size={14} aria-hidden />
                                <span>View</span>
                              </button>
                              <button
                                type="button"
                                className={`dd-action${
                                  watched ? " is-watched" : ""
                                }`}
                                disabled={watched || busy}
                                onClick={() => handleAdd(item, index)}
                              >
                                <FiHeart size={14} aria-hidden />
                                <span>
                                  {watched
                                    ? "Watching"
                                    : busy
                                    ? "Adding…"
                                    : "Add"}
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredList.length > pageSize ? (
                <div className="dd-pagination">
                  <PaginationControl
                    page={safePage}
                    between={3}
                    total={filteredList.length}
                    limit={pageSize}
                    changePage={(next) => setPage(next)}
                    ellipsis={1}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      <DetailDrawer
        item={viewingItem}
        eyebrow={`Add watchlist · ${tab.name}`}
        onClose={() => setViewingItem(null)}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  user_id: state.auth?.id,
  auctionPayload: state.product.data,
  buyPayload: state.product.databuy,
  fetchingList: state.product.fetchingList,
  fetchingBuyList: state.product.fetchingBuyList,
  wishList: state.product.wishList,
  wishListbuy: state.product.wishListbuy,
});

const mapDispatchToProps = (dispatch) => ({
  getProducts: (actionType, label, params) =>
    dispatch({
      type: actionType,
      payload: {
        type: actionType,
        method: GET,
        label: `${label}?${params}`,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
  getWishList: (userId, typeQuery, actionType) =>
    dispatch({
      type: actionType,
      payload: {
        type: actionType,
        method: GET,
        label: `${Labels.get_wish_list}${userId}/?${typeQuery}`,
        hideOverlayLoader: true,
        hideSuccessToaster: true,
      },
    }),
  createWishList: (data) =>
    dispatch({
      type: CREATE_WISH_LIST,
      payload: {
        type: CREATE_WISH_LIST,
        method: POST,
        data,
        label: Labels.create_wish_list,
        hideOverlayLoader: true,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddWatchlist);
